import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { apiFetch, translateApiError } from '../../shared/api/client';
import { CaptchaField } from './CaptchaField';

/**
 * Sign-in, sign-up and password reset.
 *
 * Three rules shape these forms:
 *
 *   1. **The sign-in error never distinguishes "no such account" from "wrong
 *      password".** The server produces one message for both, and the client
 *      must not add detail the server withheld, or the form becomes a way to
 *      discover who has an account.
 *   2. **Every word follows the interface language**, including server errors:
 *      the API answers with a stable code and the client translates it through
 *      the same catalogue as the rest of the page (section 10.2).
 *   3. **The captcha is part of the form**, not a decorative badge: the widget
 *      writes the `captcha` field the API verifies, and a form submitted before
 *      the challenge is solved is rejected by the server on purpose.
 */

export function SignInPage(): React.ReactNode {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Two different server rules can put a challenge on this page, and they are
  // not the same event:
  //   * `login_after_failures` — only after repeated failures, so the widget
  //     appears once a 401 has been seen rather than taxing every visitor;
  //   * `password_reset` — always, because "forgot password" otherwise sends
  //     mail to any address a bot names.
  // The second one is not discoverable from a failed sign-in, so the server's
  // configuration is asked for once on mount.
  const [resetCaptcha, setResetCaptcha] = useState(false);
  const [loginCaptcha, setLoginCaptcha] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const info = await apiFetch<{ actions: string[] }>('/auth/captcha/info');
        if (cancelled) return;
        setResetCaptcha(info.actions.includes('password_reset'));
      } catch {
        // If the info call fails the form still works: the server rejects a
        // missing payload, and the message it returns is translated.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);

    const data = new FormData(event.currentTarget);

    try {
      await apiFetch('/auth/login', {
        method: 'POST',
        json: {
          email,
          password,
          captcha: String(data.get('captcha') ?? '') || undefined,
        },
      });
      // A full reload keeps the cookie-based session and the SSR-shaped routes
      // in sync; navigating with the router would leave stale auth state.
      window.location.assign('/');
    } catch (cause) {
      setError(translateApiError(cause, t));
      // A failed attempt may be the fifth one; asking the server to protect the
      // next try is friendlier than letting a bot keep guessing.
      if ((cause as { status?: number }).status === 401) setLoginCaptcha(true);
    } finally {
      setBusy(false);
    }
  };

  const forgot = async (form: HTMLFormElement) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    const data = new FormData(form);
    try {
      const result = await apiFetch<{ code: string; message: string }>('/auth/forgot-password', {
        method: 'POST',
        json: {
          email,
          // The Altcha widget writes this into the form. The server requires it
          // because `password_reset` is in `captcha.enabled_on`.
          captcha: String(data.get('captcha') ?? '') || undefined,
        },
      });
      // The server sends a code so the notice is in the page's language; the
      // English message is only the fallback for an unknown code.
      setNotice(t(result.code, { defaultValue: result.message }));
    } catch (cause) {
      setError(translateApiError(cause, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-14">
      <h1 className="text-2xl font-bold">{t('auth.signInTitle')}</h1>
      <p className="mt-2 text-sm text-[var(--color-muted-fg)]">{t('auth.signInSubtitle')}</p>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <Field
          id="email"
          type="email"
          label={t('auth.email')}
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
        />
        <Field
          id="password"
          type="password"
          label={t('auth.password')}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required
        />

        {/* Only rendered when this specific action is protected. */}
        {loginCaptcha && <CaptchaField />}

        {error && <Alert tone="error">{error}</Alert>}
        {notice && <Alert tone="info">{notice}</Alert>}

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-[var(--radius-control)] bg-brand-700 px-4 py-2.5 font-semibold text-white hover:bg-brand-800 disabled:opacity-60"
        >
          {busy ? t('common.loading') : t('auth.signInAction')}
        </button>

        {/* Inside the form on purpose: "forgot password" is protected by the
            `password_reset` action, and the widget's hidden `captcha` input only
            exists within the form. When the server has that action switched off
            no widget is rendered and the button posts without a payload, which
            the server accepts. */}
        {resetCaptcha && <CaptchaField />}
        <button
          type="button"
          onClick={(event) => {
            const form = event.currentTarget.closest('form');
            if (form) void forgot(form);
          }}
          className="w-full text-center text-sm text-brand-600 hover:underline"
        >
          {t('auth.forgotPassword')}
        </button>
      </form>

      <div className="mt-4 text-center text-sm">
        <Link to="/signup" className="text-[var(--color-muted-fg)] hover:text-brand-600">
          {t('auth.noAccount')} {t('auth.signUp')}
        </Link>
      </div>
    </div>
  );
}

export function SignUpPage(): React.ReactNode {
  const { t } = useTranslation();
  const { plan } = useParams<{ plan?: string }>();

  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    organizationName: '',
    legalEntity: false,
    taxId: '',
    ageConfirmed: false,
    marketingConsent: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    // Read through `FormData`, not React state: the Altcha widget writes its
    // payload into the form imperatively and React never sees it.
    const data = new FormData(event.currentTarget);

    try {
      const result = await apiFetch<{ code: string; message: string }>('/auth/register', {
        method: 'POST',
        json: {
          email: form.email,
          password: form.password,
          firstName: form.firstName || undefined,
          organizationName: form.organizationName || undefined,
          legalEntity: form.legalEntity,
          taxId: form.taxId || undefined,
          ageConfirmed: form.ageConfirmed,
          marketingConsent: form.marketingConsent,
          captcha: String(data.get('captcha') ?? '') || undefined,
        },
      });
      setDone(t(result.code, { defaultValue: result.message }));
    } catch (cause) {
      setError(translateApiError(cause, t));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-md px-4 py-14">
        <h1 className="text-2xl font-bold">{t('auth.checkEmail')}</h1>
        <p className="mt-3 text-sm text-[var(--color-muted-fg)]">{done}</p>
        <Link
          to="/signin"
          className="mt-6 inline-block rounded-[var(--radius-control)] bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
        >
          {t('auth.signInAction')}
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-14">
      <h1 className="text-2xl font-bold">{t('auth.signUpTitle')}</h1>
      <p className="mt-2 text-sm text-[var(--color-muted-fg)]">{t('auth.signUpSubtitle')}</p>
      {plan && (
        <p className="mt-2 text-sm text-[var(--color-muted-fg)]">
          {t('auth.planSelected', { plan })}
        </p>
      )}

      <form onSubmit={submit} className="mt-6 space-y-4">
        <Field id="su-email" type="email" label={t('auth.email')} value={form.email}
          onChange={(v) => update('email', v)} autoComplete="email" required />
        <Field id="su-password" type="password" label={t('auth.password')} value={form.password}
          onChange={(v) => update('password', v)} autoComplete="new-password" required
          hint={t('auth.passwordHint', { count: 10 })} />
        <Field id="su-name" type="text" label={t('auth.firstName')} value={form.firstName}
          onChange={(v) => update('firstName', v)} autoComplete="given-name" />
        <Field id="su-org" type="text" label={t('auth.organization')} value={form.organizationName}
          onChange={(v) => update('organizationName', v)} autoComplete="organization" />

        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={form.legalEntity}
            onChange={(e) => update('legalEntity', e.target.checked)} className="mt-0.5" />
          {t('auth.legalEntity')}
        </label>

        {form.legalEntity && (
          <Field id="su-tax" type="text" label={t('auth.taxId')}
            value={form.taxId} onChange={(v) => update('taxId', v)} />
        )}

        {/* Section 23.2 requires an explicit 18+ confirmation. */}
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={form.ageConfirmed}
            onChange={(e) => update('ageConfirmed', e.target.checked)} className="mt-0.5" />
          {t('auth.ageConfirm')}
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={form.marketingConsent}
            onChange={(e) => update('marketingConsent', e.target.checked)} className="mt-0.5" />
          {t('auth.marketingConsent')}
        </label>

        <CaptchaField />

        {error && <Alert tone="error">{error}</Alert>}

        <button type="submit" disabled={busy || !form.ageConfirmed}
          className="w-full rounded-[var(--radius-control)] bg-brand-700 px-4 py-2.5 font-semibold text-white hover:bg-brand-800 disabled:opacity-60">
          {busy ? t('common.loading') : t('auth.signUpAction')}
        </button>

        <p className="text-xs text-[var(--color-muted-fg)]">
          {t('auth.legalNotice')}{' '}
          <Link to="/legal/eula" className="text-brand-600 hover:underline">
            {t('footer.eula')}
          </Link>
        </p>
      </form>
    </div>
  );
}

function Field({
  id,
  type,
  label,
  value,
  onChange,
  autoComplete,
  required,
  hint,
}: {
  id: string;
  type: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  required?: boolean;
  hint?: string;
}): React.ReactNode {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        required={required}
        className="mt-1 h-10 w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/30"
      />
      {hint && <p className="mt-1 text-xs text-[var(--color-muted-fg)]">{hint}</p>}
    </div>
  );
}

function Alert({ tone, children }: { tone: 'error' | 'info'; children: React.ReactNode }): React.ReactNode {
  const classes =
    tone === 'error'
      ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100'
      : 'border-brand-600/30 bg-brand-50 text-brand-900 dark:bg-brand-900/20';
  return (
    <p role={tone === 'error' ? 'alert' : 'status'} className={`rounded-[var(--radius-control)] border p-3 text-sm ${classes}`}>
      {children}
    </p>
  );
}
